import type { ComponentName } from "@minne/core";

/**
 * What the editor's "Insert" menu drops at the cursor: one filled-in example per
 * component, kept in step with the component guide in core. `select` is the text
 * to highlight afterwards so the first thing to change is already selected.
 */
export interface ComponentTemplate {
  name: ComponentName;
  label: string;
  hint: string;
  snippet: string;
  select: string;
}

export const COMPONENT_TEMPLATES: ComponentTemplate[] = [
  {
    name: "callout",
    label: "Callout",
    hint: "Warnings, tips, takeaways",
    snippet: '```callout tone=tip title="Worth remembering"\nSomething short and useful.\n```',
    select: "Something short and useful.",
  },
  {
    name: "checklist",
    label: "Checklist",
    hint: "Next steps you can tick off",
    snippet: "```checklist\n- [ ] First step\n- [ ] Second step\n```",
    select: "First step",
  },
  {
    name: "table",
    label: "Table",
    hint: "Comparisons",
    snippet: "```table\n| Option | Cost | Notes |\n|---|---|---|\n| One | low | |\n| Two | high | |\n```",
    select: "Option",
  },
  {
    name: "decision",
    label: "Decision",
    hint: "A choice, the options, and why",
    snippet:
      "```decision\nquestion: What are we deciding?\noptions:\n  - id: a\n    label: Option A\n    note: why it might be right\n  - id: b\n    label: Option B\nchosen: a\nrationale: One line on why.\n```",
    select: "What are we deciding?",
  },
  {
    name: "timeline",
    label: "Timeline",
    hint: "Plans, phases, history",
    snippet:
      "```timeline\nitems:\n  - date: 2026-11-01\n    title: Done already\n    status: done\n  - title: Happening now\n    body: a line about it\n    status: current\n  - title: Later\n    status: upcoming\n```",
    select: "Done already",
  },
  {
    name: "cards",
    label: "Cards",
    hint: "Options side by side",
    snippet: "```cards\ncolumns: 2\nitems:\n  - title: First\n    body: What it is.\n  - title: Second\n    body: What it is.\n    tone: tip\n```",
    select: "First",
  },
  {
    name: "stats",
    label: "Stats",
    hint: "Headline numbers",
    snippet: "```stats\nitems:\n  - label: Notes\n    value: 128\n    delta: +12 this week\n  - label: Uptime\n    value: 99.9%\n```",
    select: "Notes",
  },
  {
    name: "chart",
    label: "Chart",
    hint: "Bar, line, area or pie from numbers you supply",
    snippet: "```chart\ntype: bar\ntitle: Writes per day\nx: [Mon, Tue, Wed]\nseries:\n  - name: Claude\n    data: [4, 7, 3]\n```",
    select: "Writes per day",
  },
  {
    name: "mermaid",
    label: "Mermaid diagram",
    hint: "Flowcharts, sequences, architecture",
    snippet: "```mermaid\nflowchart LR\n  A --> B --> C\n```",
    select: "A --> B --> C",
  },
  {
    name: "columns",
    label: "Columns",
    hint: "Blocks next to each other",
    snippet: "````columns count=2\n```callout tone=tip\nLeft column.\n```\n\n```callout\nRight column.\n```\n````",
    select: "Left column.",
  },
];

/**
 * Insert a snippet at the textarea's cursor as its own paragraph: blank lines are
 * added around it as needed. Returns the new text and the selection to apply
 * (the template's `select` text if present, else the end of the snippet).
 */
export function insertSnippet(
  text: string,
  selectionStart: number,
  selectionEnd: number,
  template: Pick<ComponentTemplate, "snippet" | "select">,
): { text: string; selectionStart: number; selectionEnd: number } {
  const before = text.slice(0, selectionStart);
  const after = text.slice(selectionEnd);
  const lead = before.length === 0 ? "" : before.endsWith("\n\n") ? "" : before.endsWith("\n") ? "\n" : "\n\n";
  const trail = after.length === 0 ? "\n" : after.startsWith("\n\n") ? "" : after.startsWith("\n") ? "\n" : "\n\n";
  const inserted = `${lead}${template.snippet}${trail}`;
  const snippetStart = before.length + lead.length;
  const idx = template.select ? template.snippet.indexOf(template.select) : -1;
  const selStart = idx >= 0 ? snippetStart + idx : snippetStart + template.snippet.length;
  const selEnd = idx >= 0 ? selStart + template.select.length : selStart;
  return { text: before + inserted + after, selectionStart: selStart, selectionEnd: selEnd };
}
